import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import AuthGate from "@/components/AuthGate";
import BookmarkButton from "@/components/BookmarkButton";
import ClaimModal from "@/components/ClaimModal";
import ConfirmModal from "@/components/ConfirmModal";
import ConflictResolverModal from "@/components/ConflictResolverModal";
import CopyCommand from "@/components/CopyCommand";
import { CategoryIcon, ThreadRow } from "@/components/DiscussionBits";
import Dropdown from "@/components/Dropdown";
import ErrorNotice from "@/components/ErrorNotice";
import GithubPicker, { type GithubTarget } from "@/components/GithubPicker";
import { GithubMark, SyncStateBadge } from "@/components/GithubBits";
import NewThreadModal from "@/components/NewThreadModal";
import RatingStars from "@/components/RatingStars";
import { getGithubOrg } from "@/components/SkillCard";
import SocialShareModal from "@/components/SocialShareModal";
import SuggestionQueue from "@/components/SuggestionQueue";
import SkillPipelineProgress from "@/components/SkillPipelineProgress";
import { SkillRejectionReport } from "@/components/SkillRejectionReport";
import { trackSkillInstallCopy, trackSkillView } from "@/lib/analytics";
import {
  API_BASE,
  deleteSkill,
  downloadUrl,
  forkSkill,
  getGithubStatus,
  listGrants,
  getSkill,
  getSkillFiles,
  getVersion,
  getVersions,
  linkSkillGithub,
  listForks,
  listTeams,
  revokeGrant,
  shareWithTeam,
  listThreads,
  pushSkillGithub,
  resyncSkillGithub,
  updateGithubSettings,
  restoreVersion,
  setPublished,
  unlinkSkillGithub,
  updateSkillSettings,
} from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { describeGithubError, type DescribedError } from "@/lib/github-errors";
import { useSkillPath } from "@/lib/skillPath";
import { useSeoMeta } from "@/lib/useSeoMeta";
import { useDelayedFlag } from "@/lib/useDelayedFlag";
import { Bullet } from "@/components/Bullet";
import { SkillWorkflow } from "@/components/SkillWorkflow";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import type { Grant, Skill, SkillDetail, SkillFile, SkillWorkflowData, Team, Thread, ThreadCategory, Version } from "@/lib/types";

type Tab = "skill" | "discussions" | "suggestions" | "actions";

// Agents a skill can be installed into. Every one now reads the open Agent Skills
// (SKILL.md) standard, so the payload is identical — a target differs ONLY by the
// directory it reads from. Adding an agent is a single row here; the dropdown +
// command below are driven entirely off this list. `.agents/skills` is the
// vendor-neutral dir read by Codex, Antigravity and OpenClaw, so several agents
// point at it. Others use their own dir. `hint` is an optional sub-label.
const INSTALL_TARGETS = [
  { id: "antigravity", label: "Antigravity", dir: ".agents", hint: "shared" },
  { id: "claude", label: "Claude Code", dir: ".claude", hint: "" },
  { id: "cursor", label: "Cursor", dir: ".cursor", hint: "" },
  { id: "copilot", label: "GitHub Copilot", dir: ".github", hint: "" },
  { id: "latentcode", label: "LatentCode", dir: ".latentcode", hint: "" },
  { id: "codex", label: "OpenAI Codex", dir: ".agents", hint: "shared" },
  { id: "openclaw", label: "OpenClaw", dir: ".agents", hint: "shared" },
  { id: "opencode", label: "OpenCode", dir: ".opencode", hint: "" },
  // { id: "windsurf", label: "Windsurf", dir: ".windsurf", hint: "" },  // untested — re-enable after verifying
] as const;
type InstallTargetId = (typeof INSTALL_TARGETS)[number]["id"];

// "https://github.com/owner/repo/tree/main/…" → "github.com/owner/repo" for a compact source link.
function repoLabel(url: string): string {
  const m = url.match(/github\.com\/([^/]+)\/([^/]+)/);
  return m ? `github.com/${m[1]}/${m[2]}` : url;
}

// Browsable GitHub URL for a linked repo. When a subpath was given, deep-link into
// that exact folder on the chosen branch (…/tree/<branch>/<subpath>); otherwise the repo root.
function githubLinkUrl(gh: { repo: string; branch: string; subpath: string }): string {
  const base = `https://github.com/${gh.repo}`;
  const sub = gh.subpath?.replace(/^\/+|\/+$/g, "");
  return sub ? `${base}/tree/${gh.branch}/${sub}` : base;
}

// Compact "time since" for the archived-at tag (e.g. "3d ago").
function relAge(iso: string): string {
  const ms = Date.now() - new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z").getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// 1234 → "1.2k", 999 → "999" — GitHub-style compact counts for stars/forks.
function compactCount(n: number): string {
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")}k`;
}

const CONTRIB_LIMIT = 18; // show this many contributor chips, then a "+N" to reveal the rest

// One style for every secondary action in the install rail (.zip · Share · Suggest · Fork). They
// were four different weights — two neutral, two brand-orange — which both looked inconsistent and
// put Share/Suggest in competition with "Copy prompt", the actual primary CTA sitting right above
// them. These are all one tier below it, so they all read the same.
const RAIL_ACTION_CLS =
  "inline-flex items-center gap-1.5 rounded-lg border-[1.5px] border-primary/40 px-2.5 py-1.5 font-display " +
  "text-xs font-semibold text-text/75 transition hover:border-primary hover:bg-primary/10 hover:text-text disabled:opacity-50";

// Router `state.from` (stamped by SkillCard) → the label shown in the skill-page back crumb, so it
// reads as the place the reader came from ("My skills", "Trending", a publisher) rather than "Back".
type FromLoc = { pathname: string; search: string };
function backCrumbLabel(from?: FromLoc): string {
  if (!from) return "Back";
  const { pathname } = from;
  if (pathname === "/skills") {
    const p = new URLSearchParams(from.search);
    switch (p.get("tab") || "") {
      case "official": return p.get("publisher") || "Publisher";
      case "mine": return "My skills";
      case "trending": return "Trending";
      case "community": return "Community";
      default: return "All skills";
    }
  }
  if (pathname === "/home") return "Home";
  if (pathname.startsWith("/u/")) return `@${decodeURIComponent(pathname.slice(3))}`;
  return "Back";
}

export default function SkillPage() {
  const slug = String(useParams().skillSlug);
  const navigate = useNavigate();
  const location = useLocation();
  // Origin for the back crumb. SkillCard stamps it into router state; we persist it per-slug so it
  // survives a round trip through the file viewer (opening SKILL.md and returning re-enters this
  // page WITHOUT state). We deliberately navigate to the origin explicitly rather than history.back
  // — a plain back() would walk into the file page the reader just came from.
  const stateFrom = (location.state as { from?: FromLoc } | null)?.from;
  useEffect(() => {
    if (stateFrom) { try { sessionStorage.setItem(`sp-skill-from:${slug}`, JSON.stringify(stateFrom)); } catch { /* ignore */ } }
  }, [stateFrom, slug]);
  const backFrom: FromLoc | undefined = stateFrom ?? (() => {
    try { const raw = sessionStorage.getItem(`sp-skill-from:${slug}`); return raw ? (JSON.parse(raw) as FromLoc) : undefined; }
    catch { return undefined; }
  })();
  const backLabel = backCrumbLabel(backFrom);
  const goBack = () => navigate(backFrom ? backFrom.pathname + (backFrom.search || "") : "/skills");
  const skillPath = useSkillPath();
  const { user, accessToken, loading: authLoading } = useAuth();
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  useSeoMeta({
    title: skill ? `${skill.name} — SkillPatch` : "SkillPatch",
    description: skill?.summary,
    path: `/skill/${slug}`,
  });
  const [versions, setVersions] = useState<Version[]>([]);
  const [files, setFiles] = useState<SkillFile[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [grants, setGrants] = useState<Grant[]>([]);   // per-skill access grants (drive the Team option)
  const [teamPanelOpen, setTeamPanelOpen] = useState(false); // show the per-team access list
  const [prevTeams, setPrevTeams] = useState<string[]>([]);  // teams shared before toggling off — restored on re-enable
  const [showNoTeamModal, setShowNoTeamModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showAllContrib, setShowAllContrib] = useState(false); // collapse the contributor chips past a cap
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);
  const [versionCopied, setVersionCopied] = useState<string | null>(null);
  const [forking, setForking] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showForks, setShowForks] = useState(false);
  const [forks, setForks] = useState<Skill[]>([]);
  const [publishError, setPublishError] = useState<string | null>(null);
  // Restore can legitimately refuse — the stored archive may no longer pass intake (409),
  // or its front-matter may have no `description`, which would republish a skill no agent
  // runtime can see (422). Its own state, not the page-level `error`, which replaces the
  // whole page when it is set.
  const [restoreError, setRestoreError] = useState<DescribedError | null>(null);
  const [installTarget, setInstallTarget] = useState<InstallTargetId>("claude");
  const [installMode, setInstallMode] = useState<"prompt" | "command">("prompt");
  const [promptCopied, setPromptCopied] = useState(false);
  const [rubricOpen, setRubricOpen] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  // Only offer Show more when the summary actually overflows its 2-line clamp — a short one that
  // fits needs no toggle. Measured against the DOM (not a char count) so it's accurate at any width.
  const descRef = useRef<HTMLParagraphElement>(null);
  const [descOverflows, setDescOverflows] = useState(false);
  useEffect(() => {
    const el = descRef.current;
    if (!el || descExpanded) return; // measure only in the clamped state; keep the flag while expanded
    const check = () => setDescOverflows(el.scrollHeight > el.clientHeight + 1);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [descExpanded, skill?.summary]);
  const [selVerWorkflow, setSelVerWorkflow] = useState<SkillWorkflowData | null>(null);
  const [showClaim, setShowClaim] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [openThreads, setOpenThreads] = useState<Thread[]>([]);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadCategory, setThreadCategory] = useState<ThreadCategory | "">("");
  const [threadSort, setThreadSort] = useState<"unanswered_first" | "newest">("unanswered_first");
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const publishErrorRef = useRef<HTMLDivElement | null>(null);
  const pendingVersionIds = versions
    .filter((version) => version.status === "pending")
    .map((version) => version.id)
    .join(",");

  const load = useCallback(async () => {
    setError(null);
    try {
      const s = await getSkill(slug, accessToken);
      setSkill(s);
      setSelected(s.current_version_id);
      setVersions(await getVersions(slug, accessToken).catch(() => []));
      if (accessToken) {
        setMyTeams(await listTeams(accessToken).catch(() => []));
        // Grants power the "Team" visibility option (owner only; endpoint 403s otherwise).
        if (s.owner_id && user && String(s.owner_id) === String(user.id)) {
          setGrants(await listGrants(slug, accessToken).catch(() => []));
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Not found");
    }
  }, [slug, accessToken, user]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [load, authLoading]);

  // A restore remains non-installable until Scan → Security finishes.
  useEffect(() => {
    if (!pendingVersionIds || !accessToken) return;
    const timer = window.setInterval(async () => {
      try {
        const [nextSkill, nextVersions] = await Promise.all([
          getSkill(slug, accessToken),
          getVersions(slug, accessToken),
        ]);
        setSkill(nextSkill);
        setVersions(nextVersions);
        setSelected((previous) => (
          previous === skill?.current_version_id
            ? nextSkill.current_version_id
            : previous
        ));
      } catch {
        /* keep polling across a transient request failure */
      }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [pendingVersionIds, accessToken, slug, skill?.current_version_id]);

  // Re-fetch just the skill when the tab becomes visible again or is restored from the browser's
  // back-forward cache — so a state change made elsewhere (e.g. claiming this skill, which happens
  // via a full GitHub redirect) shows up without a manual reload. Kept light (no version/team
  // refetch) so it never disrupts the version the user is viewing.
  const refreshSkill = useCallback(async () => {
    try {
      setSkill(await getSkill(slug, accessToken));
    } catch {
      /* keep the current view on a transient failure */
    }
  }, [slug, accessToken]);

  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && refreshSkill();
    const onPageShow = (e: PageTransitionEvent) => e.persisted && refreshSkill();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [refreshSkill]);

  useEffect(() => {
    if (!selected) return;
    getSkillFiles(slug, accessToken, selected).then(setFiles).catch(() => setFiles([]));
  }, [slug, selected, accessToken]);

  // Team-sharing UI state is per-skill. The /skill/:skillSlug route reuses this component instance
  // across in-app navigation, so without this reset `prevTeams` (and `teamPanelOpen`) would leak
  // from one skill to the next — re-enabling the toggle on Skill B could silently restore Skill A's
  // teams onto B. Clear both whenever the slug changes.
  useEffect(() => {
    setTeamPanelOpen(false);
    setPrevTeams([]);
  }, [slug]);

  // Open-thread badge on the Discussions tab — fetched independently of which tab is active so
  // it's already ready the moment the tab bar renders. Scoped in the render below (owner sees the
  // full count, a non-owner thread author sees just their own, everyone else sees nothing).
  useEffect(() => {
    listThreads(slug, { sort: "newest" }, accessToken)
      .then((t) => setOpenThreads(t.filter((x) => x.status === "open")))
      .catch(() => setOpenThreads([]));
  }, [slug, accessToken]);

  // Discussions tab's full filterable list — (re)fetched on tab entry or filter/sort change.
  // Reads the tab straight from the URL (rather than the `tab` const below) so this hook can run
  // before the loading/error early-returns, which the "show"-gated `tab` value depends on (isOwner).
  const isDiscussionsTab = searchParams.get("tab") === "discussions";
  useEffect(() => {
    if (!isDiscussionsTab) return;
    setThreadsLoading(true);
    listThreads(slug, { category: threadCategory || undefined, sort: threadSort }, accessToken)
      .then(setThreads)
      .catch(() => setThreads([]))
      .finally(() => setThreadsLoading(false));
  }, [isDiscussionsTab, slug, threadCategory, threadSort, accessToken]);

  // When a NON-current version is selected, load that version's workflow so the card matches
  // the version being viewed. For the current version we use skill.workflow directly.
  useEffect(() => {
    const cur = skill?.current_version_id;
    if (!selected || !cur || selected === cur) { setSelVerWorkflow(null); return; }
    let cancelled = false;
    getVersion(slug, selected, accessToken)
      .then((v) => { if (!cancelled) setSelVerWorkflow(v.workflow ?? null); })
      .catch(() => { if (!cancelled) setSelVerWorkflow(null); });
    return () => { cancelled = true; };
  }, [slug, selected, skill?.current_version_id, accessToken]);

  useEffect(() => {
    if (publishError) publishErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [publishError]);

  // While a freshly-added skill is still in the Scan → Security review, poll so the
  // progress view advances and flips to the live skill the moment it goes `active`.
  useEffect(() => {
    if (skill?.status !== "pending") return;
    const id = setInterval(refreshSkill, 3000);
    return () => clearInterval(id);
  }, [skill?.status, refreshSkill]);

  // One skill_view per skill, not per fetch — `load` also re-runs on tab focus, bfcache restore
  // and while a pending scan polls, and none of those are a new view.
  const viewTrackedSlug = useRef<string | null>(null);
  useEffect(() => {
    if (!skill || viewTrackedSlug.current === skill.slug) return;
    viewTrackedSlug.current = skill.slug;
    trackSkillView(skill.slug, skill.security_score);
  }, [skill]);

  // Defer the loading placeholder so a fast load goes straight to content without a flash.
  const showLoading = useDelayedFlag(!skill);

  if (error) {
    // Logged out and the skill won't load (private, shared, or missing — the API returns
    // 404 for all of them): show a friendly sign-in card instead of a raw "not found".
    // A logged-in user genuinely can't see it, so keep the plain not-found message.
    if (!user) {
      return (
        <AuthGate
          title="This skill is private"
          message="Sign in to view it — or it may no longer be available."
        />
      );
    }
    return <main className="mx-auto max-w-3xl px-6 py-16 text-text/60">{error}</main>;
  }
  if (!skill) return showLoading ? <main className="mx-auto max-w-3xl px-6 py-16 text-text/50">Loading…</main> : null;

  // A skill still in — or failed by — the Scan → Security review shows the pipeline
  // progress, not the (incomplete) skill body. Only its owner ever reaches here: the
  // API hides non-active skills from everyone else.
  if (skill.status !== "active") {
    const rejected = skill.status === "rejected";
    const manualReview = skill.stage === "manual_review";
    return (
      <main className="mx-auto max-w-2xl px-6 py-12">
        <header className="text-center">
          <h1 className="font-display text-2xl font-bold tracking-tight first-letter:uppercase">{skill.name}</h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-text/55">
            {rejected
              ? "This upload didn’t pass review — it’s archived, not live."
              : manualReview
                ? "Automated review is paused. This skill remains private until a complete manual review succeeds."
              : "Running the Scan → Security review — it goes live once the checks pass."}
          </p>
          {rejected && (
            <p className="mt-2 text-xs text-text/40" title={new Date(skill.updated_at).toLocaleString()}>
              Archived {relAge(skill.updated_at)} · automatically removed after 7 days
            </p>
          )}
        </header>
        <div className="mt-8">
          <SkillPipelineProgress skill={skill} />
        </div>
        {(rejected || manualReview) && skill.rejection && (
          <SkillRejectionReport
            stage={skill.rejection.stage}
            report={skill.rejection.report}
            className="mt-6"
          />
        )}
      </main>
    );
  }

  const isOwner = !!user && user.id === skill.owner_id;
  // May review this skill's suggestions = owner OR a write-access collaborator (team co-maintainer /
  // user grant). Matches the backend's list/act guards. `isOwner` stays for owner-only surfaces
  // (Actions tab, sharing); `canReview` gates the suggestion queue + review actions.
  const canReview = !!skill.can_edit;
  // The "open discussions" badge is only meaningful (and only anyone else's business) for the
  // skill owner, who sees every open thread, or a thread author, who sees just their own.
  const visibleOpenThreadCount = isOwner
    ? openThreads.length
    : user
      ? openThreads.filter((t) => t.author_id === user.id).length
      : 0;
  // Layer 0 = community-imported (no platform owner). Such skills can be claimed by their original
  // GitHub author; consent_state tracks whether they've verified + consented.
  const isImported = skill.layer === 0;
  const isConsented = skill.consent_state === "creator_consented";
  const isCurrent = !selected || selected === skill.current_version_id;
  // Workflow card for the version being viewed: current → the detail's workflow; otherwise the
  // selected version's (fetched into selVerWorkflow).
  const activeWorkflow = isCurrent ? skill.workflow : selVerWorkflow;
  const selVer = versions.find((v) => v.id === selected)?.version ?? skill.version;
  // "Updated" date for the version being viewed: its own upload date. For the current version this
  // is the first-upload date and advances each time a merged suggestion cuts a new version; falls
  // back to the skill's updated_at before the version list has loaded.
  const updatedIso = versions.find((v) => v.id === selected)?.created_at ?? skill.updated_at;
  const updatedOn = new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(updatedIso) ? updatedIso : updatedIso + "Z")
    .toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  // `files` is already fetched per selected version, so these need no extra request. Keep the two
  // sizes distinct: the entry card is about SKILL.md itself, the rail's Contents row is the whole
  // archive — labelling the card with the archive total read as "SKILL.md is 39 KB".
  const totalFileBytes = files.reduce((sum, f) => sum + new TextEncoder().encode(f.content).length, 0);
  const skillMdBytes = new TextEncoder().encode(
    files.find((f) => f.path.toUpperCase().replace(/^.*\//, "") === "SKILL.MD")?.content ?? "",
  ).length;
  const installBase = API_BASE || (typeof window !== "undefined" ? window.location.origin : "");
  const installUrl = `${installBase}/install_skill/${slug}${isCurrent ? "" : `/${selected}`}`;
  const authHeader = skill.visibility === "public" ? "" : `-H "X-API-Key: $SKILLPATCH_API_KEY" `;
  // Same install, just dropped into the selected agent's project-level skills dir (.claude, .latentcode, …).
  const installDir = (INSTALL_TARGETS.find((t) => t.id === installTarget) ?? INSTALL_TARGETS[0]).dir;
  const installCmd = `mkdir -p ${installDir}/skills
curl -sSL ${authHeader}${installUrl} | tar -xz -C ${installDir}/skills/ && echo "✓ Successfully installed ${slug}"`;
  // Prompt-based install: a copy-paste block a user can hand to any AI agent so it reviews the
  // skill page and installs it. Points only at SkillPatch (our download command) — no GitHub link.
  // The command ONLY downloads the archive; extraction and placement are left to the agent so it
  // can drop the skill into whichever project-level skills dir fits its environment.
  const skillPageUrl = `${installBase}/skill/${slug}`;
  const promptDownloadCmd = `curl -sSL ${installUrl} -o ${slug}.tar.gz`;
  const creatorLine = skill.owner_name ? `\nCreator: ${skill.owner_name}` : "";
  // Private skills authorize via the one-time setup (which stores the API key in the
  // environment), so the download command stays clean — we never inline the key.
  const privateNote =
    skill.visibility !== "public"
      ? "\nNote: this is a private skill — if you haven't already, complete the one-time SkillPatch setup so this environment is authorized to download it.\n"
      : "";
  const installPrompt = `Install this Agent Skill into my current project.

Skill: ${skill.name}
Skill page: ${skillPageUrl}${creatorLine}
Download command: ${promptDownloadCmd}
${privateNote}
Steps:
1. Run the download command to fetch ${slug}.tar.gz.
2. Extract it (tar -xzf ${slug}.tar.gz) — it unpacks to a ${slug}/ folder containing SKILL.md and its companion files.
3. Move that whole ${slug}/ folder into this tool's OWN dedicated project-level skills directory — the folder native to the agent you are (prefer your own native skills folder over any shared or cross-tool compatibility folder), never a global or home path. Keep the folder intact and replace any existing ${slug}/.
4. Delete ${slug}.tar.gz. Confirm ${slug}/SKILL.md is in place and that you can now use the skill (reload the tool if it only detects skills at startup), then tell me in one line what it does.`;

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(installPrompt);
      trackSkillInstallCopy(slug, "prompt");
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }
  const dlUrl = isCurrent ? downloadUrl(slug) : `${API_BASE}/skills/${slug}/versions/${selected}/download`;
  // A plain <a href> can't carry the Authorization header, so a private skill's download would
  // 404 ("skill not found" — the same response used to hide a private skill's existence from an
  // unauthenticated request). Fetch with the token attached instead, then trigger the save via a
  // throwaway blob link.
  const handleDownload = async () => {
    setDownloadError(null);
    try {
      const headers: HeadersInit = {};
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      const res = await fetch(dlUrl, { headers });
      if (!res.ok) throw new Error(res.status === 404 ? "Skill not found" : `Download failed (${res.status})`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = isCurrent ? `${slug}.zip` : `${slug}-v${selVer}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      trackSkillInstallCopy(slug, "zip");
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Download failed");
    }
  };

  const TABS: { key: Tab; label: string; show: boolean }[] = [
    { key: "skill", label: "Overview", show: true },
    { key: "discussions", label: "Discussions", show: true },
    { key: "suggestions", label: "Suggestions", show: !!user },
    { key: "actions", label: "Actions", show: isOwner },
  ];

  // Persist the active tab in the URL (?tab=) so a refresh restores it instead of resetting to
  // "skill". Fall back to "skill" if the requested tab isn't available to this user.
  const requestedTab = searchParams.get("tab") as Tab | null;
  const tab: Tab = TABS.find((t) => t.key === requestedTab && t.show)?.key ?? "skill";
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(searchParams);
    if (t === "skill") next.delete("tab"); // default tab → keep the URL clean
    else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  function onSelectVersion(uid: string) {
    setSelected(uid);
  }
  async function onRestore(uid: string) {
    if (!accessToken) return;
    setBusy(true);
    setRestoreError(null);
    try {
      const candidate = await restoreVersion(slug, uid, accessToken);
      setExpandedVersion(candidate.id);
      await load();
    } catch (e) {
      // ⚠️ Without this the button clears its spinner and does nothing at all: the throw
      // becomes an unhandled rejection and the owner is left guessing whether it worked.
      const detail = e instanceof Error ? e.message : "";
      setRestoreError({
        tone: "warning",
        title: "This version can’t be restored",
        detail: detail || "The server refused the restore.",
        hint: detail.includes("description")
          ? "Publish a new version whose SKILL.md front-matter has a 'description' line — agents read that field to decide when to load a skill, so restoring this one would make it invisible."
          : undefined,
      });
    } finally {
      setBusy(false);
    }
  }
  // Public / Private only — independent of team sharing (never touches grants).
  async function setVisibility(v: "public" | "private") {
    if (!accessToken || !skill || skill.visibility === v) return;
    setBusy(true); setPublishError(null);
    try {
      await setPublished(slug, v === "public", accessToken);
      await load();
    } catch (e) {
      if (v === "public") setPublishError(e instanceof Error ? e.message : "Could not publish");
      else setError(e instanceof Error ? e.message : "Could not unpublish");
    } finally {
      setBusy(false);
    }
  }

  // Master "Team" toggle — independent of Public/Private. Off → remember the shared teams and remove
  // their access. On → restore the teams it was shared with previously (or open the picker first time).
  async function toggleTeamSharing() {
    if (!accessToken || !skill) return;
    const teamGrants = grants.filter((g) => g.kind === "team");
    const enabled = teamGrants.length > 0 || teamPanelOpen;
    if (enabled) {
      const shared = teamGrants.map((g) => g.slug).filter((s): s is string => !!s);
      if (shared.length) setPrevTeams(shared);
      setBusy(true); setError(null);
      try {
        for (const g of teamGrants) await revokeGrant(slug, "team", g.id, accessToken).catch(() => {});
        await load();
        setTeamPanelOpen(false);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (myTeams.length === 0) { setShowNoTeamModal(true); return; }
    setTeamPanelOpen(true);
    const restore = prevTeams.filter((s) => myTeams.some((t) => t.slug === s));
    if (restore.length) {
      setBusy(true); setError(null);
      try {
        for (const s of restore) await shareWithTeam(slug, s, "read", accessToken).catch(() => {});
        await load();
      } finally {
        setBusy(false);
      }
    }
  }

  // Give / remove a single team's access. Does not change Public/Private.
  async function toggleTeamAccess(team: Team, has: boolean) {
    if (!accessToken || !skill) return;
    setBusy(true); setError(null);
    try {
      if (has) {
        const g = grants.find((x) => x.kind === "team" && x.slug === team.slug);
        if (g) await revokeGrant(slug, "team", g.id, accessToken);
      } else {
        await shareWithTeam(slug, team.slug, "read", accessToken);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update team access");
    } finally {
      setBusy(false);
    }
  }
  async function onFork() {
    if (!accessToken) return;
    setForking(true);
    try {
      const forked = await forkSkill(slug, accessToken);
      navigate(skillPath(forked.slug));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fork failed");
      setForking(false);
    }
  }

  async function copyVersionItem(text: string, key: string) {
    await navigator.clipboard.writeText(text).catch(() => {});
    setVersionCopied(key);
    setTimeout(() => setVersionCopied(null), 1500);
  }

  async function shareVersion(platform: "linkedin" | "x", vId: string, vLabel: string) {
    if (!skill) return;
    const versionInstallCmd = `curl -sSL ${installBase}/install_skill/${slug}/${vId} | tar -xz -C ${installDir}/skills/`;
    const xText = `Check out v${vLabel} of "${skill.name}" on SkillPatch!\n\n${skillPageUrl}`;
    const linkedinText = [
      `I'm sharing v${vLabel} of "${skill.name}" — a Claude skill on SkillPatch.`,
      skill.summary ? `\n${skill.summary}` : "",
      `\nInstall v${vLabel} directly:\n${versionInstallCmd}`,
      `\nExplore more skills: ${skillPageUrl}`,
    ].join("\n");

    if (platform === "linkedin") {
      await navigator.clipboard.writeText(linkedinText).catch(() => {});
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(skillPageUrl)}`,
        "_blank",
        "noopener,noreferrer",
      );
    } else {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(xText)}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
  }

  async function onToggleSuggestions() {
    if (!accessToken || !skill) return;
    setSavingSettings(true);
    try {
      const updated = await updateSkillSettings(slug, { accept_suggestions: !skill.accept_suggestions }, accessToken);
      setSkill(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed");
    } finally {
      setSavingSettings(false);
    }
  }

  async function onToggleDiscussions() {
    if (!accessToken || !skill) return;
    setSavingSettings(true);
    try {
      const updated = await updateSkillSettings(slug, { discussions_enabled: !skill.discussions_enabled }, accessToken);
      setSkill(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed");
    } finally {
      setSavingSettings(false);
    }
  }

  async function onDelete() {
    if (!accessToken) return;
    setBusy(true);
    try {
      await deleteSkill(slug, accessToken);
      navigate("/home");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
      setBusy(false);
    }
  }

  const visCls =
    skill.visibility === "public"
      ? "border-green-500/30 bg-green-500/10 text-green-400"
      : "border-yellow-500/30 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400";
  // Flat neutral mono pill used for the tag row (design-system's chip-neutral).
  const chipNeutral = "inline-flex items-center gap-1.5 rounded-[3px] border border-white/[0.14] bg-white/[0.03] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide text-text/55";

  return (
    <div className="flex flex-col">
      {/* Shared header — Back link, title/chips/security, description, tags, stats, then the
          tab bar. Shared across every tab (previously this only rendered on the "skill" tab). The
          page scrolls with the document (no nested scroll region), so the footer sits naturally
          below the content instead of floating under a viewport-locked pane. */}
      <div className="shrink-0 border-b border-white/10 bg-white/[0.015] px-6 pt-4">
        {/* Title + badges, with an orange back button just before the title. It returns to wherever
            the reader came from (see goBack), falling back to /skills on a cold direct load. */}
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={goBack}
              aria-label="Back"
              title={backFrom ? `Back to ${backLabel}` : "Back"}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/40 text-primary transition-colors hover:border-primary hover:bg-primary/10"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-xl font-medium tracking-tight text-text first-letter:uppercase">{skill.name}</h1>
              {skill.security?.verified && (
                <VerifiedBadge iconOnly onSeeRubric={() => setRubricOpen(true)} />
              )}
              {skill.is_default && (
                <span title="Default — your admin gives this skill to every account (read-only)" className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                  Default
                </span>
              )}
            </div>
            {/* Visibility moved to the top-right cluster with bookmark; only the flag (rare, and a
                warning the reader must not miss) still sits inline under the title. */}
            {skill.security?.flagged && !skill.security?.verified && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span title="Security concerns detected — review rubric for details" className="rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-0.5 text-xs font-medium text-yellow-600 dark:text-yellow-400">
                  ⚠️ Flagged by SkillPatch
                </span>
              </div>
            )}
            {skill.owner_name && (
              <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text/50">
                <span>
                  {/* Link the creator: an official (imported) skill → its publisher gallery on Explore;
                      an owned skill → the author's public profile (when they have a handle). */}
                  Created by{" "}
                  {isImported ? (
                    <Link
                      to={`/skills?tab=official&publisher=${encodeURIComponent(getGithubOrg(skill.original_source_url) ?? skill.owner_name ?? "")}`}
                      className="font-semibold text-primary transition-colors hover:underline"
                    >
                      {skill.owner_name}
                    </Link>
                  ) : skill.owner_username ? (
                    <Link to={`/u/${skill.owner_username}`} className="font-semibold text-primary transition-colors hover:underline">
                      {skill.owner_name}
                    </Link>
                  ) : (
                    <span className="font-semibold text-primary">{skill.owner_name}</span>
                  )}
                </span>
                {skill.github && (
                  <>
                    <Bullet size="h-1 w-1" className="opacity-40" />
                    <a
                      href={githubLinkUrl(skill.github)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`${skill.github.repo}@${skill.github.branch}${skill.github.subpath ? `/${skill.github.subpath}` : ""} · ${skill.github.sync_state}`}
                      className="inline-flex items-center gap-1 font-semibold text-primary transition hover:underline"
                    >
                      <GithubMark size={12} /> GitHub
                    </a>
                  </>
                )}
              </p>
            )}
            {skill.fork_of && skill.fork_parent_name && (
              <p className="mt-1 text-xs text-text/40">
                Forked from <span className="text-text/60">{skill.fork_parent_name}</span>
                {skill.fork_intent && <span className="ml-1">· {skill.fork_intent}</span>}
              </p>
            )}
            {isImported && skill.original_source_url && (
              <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text/40">
                <a
                  href={skill.original_source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={repoLabel(skill.original_source_url)}
                  aria-label={`View source on GitHub: ${repoLabel(skill.original_source_url)}`}
                  className="inline-flex items-center gap-1 font-semibold text-primary transition hover:underline"
                >
                  <GithubMark size={13} /> GitHub
                </a>
                <Bullet size="h-1 w-1" className="opacity-40" />
                <span>MIT License</span>
                {skill.github_stats && (
                  <>
                    <Bullet size="h-1 w-1" className="opacity-40" />
                    <span title={`${skill.github_stats.stars.toLocaleString()} GitHub stars`}>
                      ★ {compactCount(skill.github_stats.stars)}
                    </span>
                    <Bullet size="h-1 w-1" className="opacity-40" />
                    <span title={`${skill.github_stats.forks.toLocaleString()} GitHub forks`}>
                      ⑂ {compactCount(skill.github_stats.forks)}
                    </span>
                  </>
                )}
                <Bullet size="h-1 w-1" className="opacity-40" />
                {/* Claim lives inline here rather than in a banner card of its own — it's a
                    one-line ask aimed at one rare visitor, so it shouldn't outrank the content. */}
                {isConsented ? (
                  <span
                    title={`Claimed by ${skill.consented_by || "the original author"} — verified via GitHub`}
                    className="inline-flex items-center gap-1 font-medium text-green-400"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    Claimed
                  </span>
                ) : (
                  <>
                    <span>Unclaimed</span>
                    <Bullet size="h-1 w-1" className="opacity-40" />
                    <button
                      type="button"
                      onClick={() => setShowClaim(true)}
                      className="font-medium text-primary transition hover:underline"
                    >
                      Is this your skill? Claim it
                    </button>
                  </>
                )}
              </p>
            )}
            </div>
          </div>
          {/* Top-right cluster: visibility + bookmark. Pairing them gives each something to sit
              with, which is what the lone bookmark button was missing. The security score stays in
              the install rail, next to the action it gates. */}
          <div className="flex shrink-0 items-center gap-2">
            <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${visCls}`}>{skill.visibility}</span>
            <BookmarkButton slug={skill.slug} bookmarked={skill.bookmarked} size="md" matchTitle />
          </div>
        </div>

        {/* Description — clamped to 2 lines by default; "Show more" expands it in place. */}
        {skill.summary && (
          <div className="mt-2.5">
            <p ref={descRef} className={`text-sm leading-relaxed text-text/70 ${descExpanded ? "" : "line-clamp-2"}`}>{skill.summary}</p>
            {(descOverflows || descExpanded) && (
              <button
                onClick={() => setDescExpanded((v) => !v)}
                className="mt-1 font-display text-xs font-medium text-text/45 transition hover:text-text/70"
              >
                {descExpanded ? "Show less" : "Show more"}
              </button>
            )}
          </div>
        )}
        {skill.tags.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {skill.tags.map((t) => (
              <span key={t} className={chipNeutral}>{t}</span>
            ))}
          </div>
        )}

        {/* Tab bar — below the header, last element of the shared header. Rating / downloads /
            bookmarks / forks now live in the Version card in the install rail. */}
        <nav className="mt-6 flex items-center gap-7">
          {TABS.filter((t) => t.show).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`relative pb-2.5 font-display text-sm font-medium transition ${
                tab === t.key ? "text-text" : "text-text/50 hover:text-text/80"
              }`}
            >
              {t.label}
              {t.key === "discussions" && visibleOpenThreadCount > 0 && (
                <span
                  title={`${visibleOpenThreadCount} open ${visibleOpenThreadCount === 1 ? "discussion" : "discussions"}`}
                  className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 font-mono text-[9px] font-bold leading-none text-white"
                >
                  {visibleOpenThreadCount}
                </span>
              )}
              {tab === t.key && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
            </button>
          ))}
        </nav>
      </div>

      {/* ── SKILL (OVERVIEW) TAB ── */}
      {tab === "skill" && (
        <main className="flex-1 px-6 py-7 sm:px-8">
          {/* Two kinds of object, treated differently:
                · Install + Security are ACTIONS — they must stay glanceable, so they live in a rail
                  pinned from the very top of the content area rather than scrolling away.
                · Workflow + SKILL.md + contents are READING — a single column, scanned top-down.
              The rail is first in the DOM so a phone (one column) gets the plain linear order:
              Install → Security → Workflow → the skill → contents. `lg:order-*` moves it right. */}
          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            {/* Deliberately NOT sticky. Pinning the rail while the left column scrolled made one
                half of the page look frozen against the other. Sticky was there to keep Install
                reachable — but now that the rail starts at the top of the content area, Install is
                already above the fold on load, so it was buying an oddity for nothing. (To bring it
                back: add `lg:sticky lg:top-[88px]` — 72px SiteHeader + 16px gap; a plain top-4 pins
                it under the header and clips the card's title.) */}
            <aside className="order-1 flex min-w-0 flex-col gap-4 lg:order-2">
              {/* ── Install ── */}
              {/* NOT overflow-hidden: the agent picker in Command mode is a Dropdown, whose menu is
                  absolutely positioned, so any clipping ancestor swallows it. The rounded corners
                  hold up without it — the header/footer rows carry borders, not their own fill. */}
              <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)]" style={{ boxShadow: "var(--shadow-card)" }}>
                <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] px-4 py-3">
                  <h2 className="font-display text-sm font-bold tracking-tight text-text">Install</h2>
                  <span className={`flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${isCurrent ? "bg-green-500/10 text-green-400" : "bg-white/[0.06] text-text/60"}`}>
                    {isCurrent && <span className="h-1.5 w-1.5 rounded-full bg-green-400" />}
                    v{selVer}
                  </span>
                </div>

                <div className="p-4">
                  {skill.visibility !== "public" && (
                    <p className="mb-3 text-xs leading-relaxed text-text/55">
                      Private skill — installing needs your API key. One-time{" "}
                      <Link to="/settings" className="text-primary hover:underline">setup in Settings</Link>.
                    </p>
                  )}

                  <div className="flex rounded-lg border border-[var(--border)] bg-black/[0.03] p-0.5 text-xs font-medium dark:bg-white/[0.04]">
                    {(["prompt", "command"] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setInstallMode(m)}
                        className={`flex-1 rounded-md px-3 py-1.5 capitalize transition ${
                          installMode === m ? "bg-primary text-white shadow-sm" : "text-text/55 hover:text-text/80"
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>

                  {installMode === "prompt" ? (
                    <div className="mt-3">
                      <p className="text-xs leading-relaxed text-text/55">
                        Paste this into Claude, Codex, Cursor or another assistant and it installs the
                        skill for you.
                      </p>
                      <button
                        type="button"
                        onClick={copyPrompt}
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-hover"
                      >
                        {promptCopied ? (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                        ) : (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                        )}
                        {promptCopied ? "Copied" : "Copy prompt"}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-3">
                      {INSTALL_TARGETS.length > 1 && (
                        <Dropdown
                          value={installTarget}
                          onChange={(v) => setInstallTarget(v as InstallTargetId)}
                          ariaLabel="Install target agent"
                          size="sm"
                          accent
                          className="mb-2 w-full"
                          options={INSTALL_TARGETS.map((t) => ({ value: t.id, label: t.label }))}
                        />
                      )}
                      <CopyCommand command={installCmd} onCopy={() => trackSkillInstallCopy(slug, "command")} />
                    </div>
                  )}
                </div>

                {/* Secondary actions, kept with Install so every "get this skill" verb is in one place. */}
                <div className="flex flex-wrap items-center gap-1.5 border-t border-[var(--border)] px-4 py-3">
                  <button type="button" onClick={handleDownload} className={RAIL_ACTION_CLS}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                    {isCurrent ? "zip file" : `v${selVer}`}
                  </button>
                  {(skill.status === "active" || isOwner) && (
                    <SocialShareModal
                      skill={skill}
                      slug={slug}
                      myTeams={myTeams}
                      accessToken={accessToken}
                      isOwner={isOwner}
                      triggerClassName={RAIL_ACTION_CLS}
                    />
                  )}
                  {user && skill.accept_suggestions && (
                    <Link to={skillPath(slug, "/suggest")} className={RAIL_ACTION_CLS}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" /></svg>
                      Suggest
                    </Link>
                  )}
                  {user && !isOwner && skill.status === "active" && !skill.is_default && (
                    <button onClick={onFork} disabled={forking} className={RAIL_ACTION_CLS}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="18" r="3" /><circle cx="6" cy="6" r="3" /><circle cx="18" cy="6" r="3" />
                        <path d="M18 9v1a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9M12 15v-3" />
                      </svg>
                      {forking ? "Forking…" : "Fork"}
                    </button>
                  )}
                  {downloadError && <span className="w-full text-xs text-red-400">{downloadError}</span>}
                </div>
              </div>

              {/* ── Security — directly under the install action it gates ── */}
              {skill.security && (
                <SecurityRailCard security={skill.security} onOpen={() => setRubricOpen(true)} />
              )}

              {/* ── Version + contents ── */}
              <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] px-4 py-3 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display font-semibold text-text">Downloads</span>
                  <span className="font-mono text-text">{skill.metrics.downloads}</span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2 border-t border-[var(--border)] pt-2">
                  <span className="font-display font-semibold text-text">Bookmarks</span>
                  <span className="font-mono text-text">{skill.bookmark_count}</span>
                </div>
                {skill.rating.count > 0 && (
                  <div className="mt-2 flex items-center justify-between gap-2 border-t border-[var(--border)] pt-2">
                    <span className="font-display font-semibold text-text">Rating</span>
                    <span className="inline-flex items-center gap-1 font-mono text-text">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none" className="text-accent">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                      {skill.rating.avg.toFixed(1)} <span className="text-text/45">({skill.rating.count})</span>
                    </span>
                  </div>
                )}
                {skill.fork_count > 0 && (
                  <div className="mt-2 flex items-center justify-between gap-2 border-t border-[var(--border)] pt-2">
                    <span className="font-display font-semibold text-text">Forks</span>
                    <button
                      onClick={() => { listForks(slug).then(setForks).catch(() => setForks([])); setShowForks(true); }}
                      className="font-mono text-primary transition hover:underline"
                    >
                      {skill.fork_count}
                    </button>
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between gap-2 border-t border-[var(--border)] pt-2">
                  <span className="font-display font-semibold text-text">Version</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-text">v{selVer}</span>
                    {versions.length > 1 && (
                      <button onClick={() => setShowHistory(true)} className="text-primary transition hover:underline">
                        History
                      </button>
                    )}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2 border-t border-[var(--border)] pt-2">
                  <span className="font-display font-semibold text-text">Contents</span>
                  <span className="font-mono text-text">
                    {files.length} {files.length === 1 ? "file" : "files"} · {formatBytes(totalFileBytes)}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2 border-t border-[var(--border)] pt-2">
                  <span className="font-display font-semibold text-text">Updated</span>
                  <span className="font-mono text-text">{updatedOn}</span>
                </div>
                {!isCurrent && (
                  <button
                    onClick={() => setSelected(skill.current_version_id)}
                    className="mt-2 w-full rounded-lg border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary transition hover:bg-primary/20"
                  >
                    ↑ Back to current version
                  </button>
                )}
              </div>

              {/* ── Who built it ── sits with the other "about this skill" facts rather than
                   stranded under the content column. */}
              {skill.contributors.length > 0 && (
                <div id="skill-contributors" className="scroll-mt-24 rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] px-4 py-3">
                  <p className="font-display text-xs font-semibold text-text">
                    Contributors <span className="font-mono font-normal text-text/50">({skill.contributors.length})</span>
                  </p>
                  <ul className="mt-2.5 flex flex-wrap gap-1.5">
                    {(showAllContrib ? skill.contributors : skill.contributors.slice(0, CONTRIB_LIMIT)).map((c) => {
                      const chipCls = "flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] py-0.5 pl-0.5 pr-2.5";
                      const inner = (
                        <>
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/20 text-[10px] font-semibold text-accent">
                            {(c.name || "?").charAt(0).toUpperCase()}
                          </span>
                          <span className="text-xs text-text">{c.name || "Unknown"}</span>
                        </>
                      );
                      return (
                        <li key={c.id}>
                          {c.username ? (
                            <Link to={`/u/${c.username}`} className={`${chipCls} transition hover:border-primary/40 hover:bg-primary/10`} title={`View @${c.username}'s profile`}>
                              {inner}
                            </Link>
                          ) : (
                            <span className={chipCls}>{inner}</span>
                          )}
                        </li>
                      );
                    })}
                    {!showAllContrib && skill.contributors.length > CONTRIB_LIMIT && (
                      <li>
                        <button
                          type="button"
                          onClick={() => setShowAllContrib(true)}
                          className="flex h-6 items-center rounded-full border border-primary/30 bg-primary/10 px-2.5 text-xs font-semibold text-primary transition hover:bg-primary/20"
                        >
                          +{skill.contributors.length - CONTRIB_LIMIT}
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </aside>

            <section className="order-2 flex min-w-0 flex-col gap-6 lg:order-1">
              {/* Workflow — the human-readable answer to "what does this do for me". */}
              {activeWorkflow && (
                <div className="rounded-2xl border border-black/10 bg-black/[0.02] dark:border-white/10 dark:bg-white/[0.02]">
                  {/* On mobile the icon + title + version badge sit on one row, and the subtitle drops
                      to its own line below — cramming all four inline overflowed on a phone. */}
                  <div className="border-b border-black/10 px-5 py-3.5 dark:border-white/10">
                    <div className="flex items-center gap-2.5">
                      <svg width="14" height="14" viewBox="0 0 16 16" className="shrink-0 text-primary"><path d="M8.5 1L2 8.5h4L5.5 15L12 6.5H8L8.5 1z" fill="currentColor" /></svg>
                      <h2 className="text-base font-bold tracking-tight text-text">Workflow</h2>
                      <span className="hidden text-sm font-normal text-text/40 sm:inline">— How this skill works</span>
                      {/* which version this card reflects (driven by the Version history selection) */}
                      <span className="ml-auto shrink-0 rounded-md border border-black/10 bg-black/[0.03] px-2 py-0.5 text-xs font-medium text-text/50 dark:border-white/10 dark:bg-white/[0.05]">
                        v{selVer}{isCurrent ? " · latest" : ""}
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm font-normal text-text/40 sm:hidden">How this skill works</p>
                  </div>
                  <div className="p-5">
                    <SkillWorkflow key={selected ?? "current"} data={activeWorkflow} embedded />
                  </div>
                </div>
              )}

              {/* The source. A SKILL.md is written for the AGENT to execute — imperative, long,
                  full of phases and tables — so rendering it inline buried the page in text that
                  isn't really aimed at the reader. It gets a real entry point instead of the
                  file-tree row it used to be, and opens in the full-width reader. */}
              <button
                type="button"
                onClick={() => navigate(skillPath(slug, "/SKILL.md"))}
                className="group flex w-full items-center gap-4 rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] px-5 py-4 text-left transition hover:border-primary/40"
                style={{ boxShadow: "var(--shadow-card)" }}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-primary">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
                    <path d="M8 13h8M8 17h5" />
                  </svg>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-mono text-sm text-text">SKILL.md</span>
                    {skillMdBytes > 0 && (
                      <span className="font-mono text-[11px] text-text/40">{formatBytes(skillMdBytes)}</span>
                    )}
                  </span>
                  <span className="mt-1 block text-xs leading-relaxed text-text/50">
                    The full instruction set this skill installs into your agent
                    {files.length > 1 && `, plus ${files.length - 1} bundled ${files.length === 2 ? "file" : "files"}`}.
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5 rounded-lg border-[1.5px] border-primary/40 px-3 py-2 font-display text-xs font-semibold text-text/75 transition group-hover:border-primary group-hover:bg-primary/10 group-hover:text-primary">
                  Read the skill
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                </span>
              </button>

              {/* No separate "browse files" link: it pointed at the same reader the card above
                  opens, and that reader already has the full file tree in its sidebar. */}
            </section>
          </div>
        </main>
      )}

      {/* ── DISCUSSIONS TAB ── */}
      {tab === "discussions" && (
        <main className="flex-1 px-6 py-8 sm:px-8">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-semibold tracking-tight">Discussions</h2>
                <p className="mt-1 text-sm text-text/50">Questions and bug reports for this skill.</p>
              </div>
              {skill.discussions_enabled && (
                <button
                  onClick={() => (user ? setNewThreadOpen(true) : navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`))}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:opacity-90"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                  New discussion
                </button>
              )}
            </div>

            {!skill.discussions_enabled && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text">Currently discussions are off</p>
                  <p className="mt-0.5 text-xs text-text/60">New threads and replies are paused. Existing discussions stay visible below.</p>
                </div>
                {isOwner && (
                  <button
                    onClick={() => setTab("actions")}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:opacity-90"
                  >
                    Turn on
                  </button>
                )}
              </div>
            )}

            {/* The category is a label the author picks when opening a thread (and it shows as a
                chip on every row), so a second row of category *tabs* underneath the page tabs was
                the same choice twice. One filter + one sort instead. */}
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-2">
              <span className="text-xs text-text/40">
                {threadsLoading ? "Loading…" : `${threads.length} ${threads.length === 1 ? "thread" : "threads"}`}
              </span>
              <div className="flex items-center gap-2">
                <Dropdown
                  value={threadCategory}
                  onChange={(v) => setThreadCategory(v as ThreadCategory | "")}
                  ariaLabel="Filter discussions by label"
                  align="right"
                  size="sm"
                  className="w-36"
                  options={[
                    { value: "", label: "All labels" },
                    { value: "question", label: "Questions" },
                    { value: "bug", label: "Bug reports" },
                  ]}
                />
                <Dropdown
                  value={threadSort}
                  onChange={(v) => setThreadSort(v as "unanswered_first" | "newest")}
                  ariaLabel="Sort discussions"
                  align="right"
                  size="sm"
                  className="w-44"
                  options={[
                    { value: "unanswered_first", label: "Unanswered first" },
                    { value: "newest", label: "Newest" },
                  ]}
                />
              </div>
            </div>

            {!threadsLoading && threads.length === 0 ? (
              <div className="mt-8 flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/15 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-text/40">
                  <CategoryIcon category={threadCategory || "question"} className="h-5 w-5" />
                </span>
                <p className="text-sm font-medium text-text">
                  {threadCategory === "bug" ? "No reported issues" : "No discussions yet"}
                </p>
                <p className="max-w-sm text-xs leading-relaxed text-text/45">
                  {threadCategory === "bug"
                    ? "No bugs have been reported for this skill — a good sign."
                    : "Questions and bug reports for this skill will show up here."}
                </p>
              </div>
            ) : (
              <div className="mt-2">
                {threads.map((t) => (
                  <ThreadRow key={t.id} thread={t} href={skillPath(slug, `/discussions/${t.id}`)} />
                ))}
              </div>
            )}

            {/* Ratings live here rather than on Overview: leaving a rating is feedback, same as
                opening a thread. The aggregate score stays in the page header, where it's part of
                the install decision. */}
            <div className="mt-10 border-t border-white/10 pt-6">
              <Eyebrow>Rate this skill</Eyebrow>
              <div className="mt-3">
                <RatingStars
                  slug={slug}
                  onRated={(s) =>
                    setSkill((prev) => (prev ? { ...prev, rating: { avg: s.avg, count: s.count } } : prev))
                  }
                />
              </div>
            </div>
          </div>
        </main>
      )}

      {/* ── SUGGESTIONS TAB ── */}
      {tab === "suggestions" && user && (
        <main className="flex-1 px-6 py-8 sm:px-8">
          <div>
            <h2 className="font-display text-lg font-semibold tracking-tight">Suggestions</h2>
            <p className="mt-1 text-sm text-text/50">
              {canReview
                ? "Proposed changes to this skill — each reviewed by agents before you approve the merge."
                : "Approved changes that have been reviewed and merged into this skill."}
            </p>
            <div className="mt-5">
              <SuggestionQueue slug={slug} canReview={canReview} />
            </div>
          </div>
        </main>
      )}

      {/* ── FORKS PANEL ── */}
      {showForks && (
        <div
          className="fixed inset-0 z-[100] overflow-y-auto bg-black/70 backdrop-blur-sm"
          onClick={() => setShowForks(false)}
        >
          <div className="flex min-h-full items-start justify-center p-6 pt-16">
            <div
              className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#111] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                <div>
                  <h3 className="font-display font-semibold">Forks</h3>
                  <p className="text-xs text-text/40">{skill.fork_count} public {skill.fork_count === 1 ? "fork" : "forks"} of {skill.name}</p>
                </div>
                <button
                  onClick={() => setShowForks(false)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-text/40 transition hover:bg-white/10 hover:text-text"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-5" style={{ maxHeight: "65vh", overflowY: "auto" }}>
                {forks.length === 0 && skill.fork_count === 0 ? (
                  <p className="py-4 text-sm text-text/40">No public forks yet.</p>
                ) : (
                  <ForkTree
                    name={skill.name}
                    parentName={skill.fork_parent_name}
                    forks={forks}
                    forkCount={skill.fork_count}
                    pathFor={skillPath}
                    onOpen={() => setShowForks(false)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SECURITY RUBRIC MODAL ── */}
      {/* Rendered once at page level (not inside a tab) so the rail card, the verified badge and
          any other trigger all open the same instance regardless of which tab is showing. */}
      {skill.security && (
        <SecurityRubric security={skill.security} open={rubricOpen} onOpenChange={setRubricOpen} />
      )}

      {/* ── CLAIM (creator consent / takedown) MODAL ── */}
      {showClaim && <ClaimModal slug={slug} onClose={() => setShowClaim(false)} />}

      {/* No-team prompt when picking "Team" visibility without belonging to a team. */}
      {showNoTeamModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setShowNoTeamModal(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] p-6 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-base font-semibold">You're not part of a team yet</h3>
            <p className="mt-2 text-sm text-text/55">Create a team first, then you can share this skill with it.</p>
            <div className="mt-5 flex justify-center gap-2">
              <button onClick={() => setShowNoTeamModal(false)} className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm text-text/70 transition hover:bg-[var(--glass-strong)]">Cancel</button>
              <Link to="/home?section=teams" onClick={() => setShowNoTeamModal(false)} className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-hover">Create a team</Link>
            </div>
          </div>
        </div>
      )}

      {/* ── NEW DISCUSSION MODAL ── */}
      {newThreadOpen && accessToken && (
        <NewThreadModal
          slug={slug}
          accessToken={accessToken}
          onClose={() => setNewThreadOpen(false)}
          onCreated={(t) => {
            setNewThreadOpen(false);
            navigate(skillPath(slug, `/discussions/${t.id}`));
          }}
        />
      )}

      {/* ── VERSION HISTORY MODAL ── */}
      {showHistory && (() => {
        const _ts = (s: string) => new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + "Z");
        const detailId = expandedVersion ?? skill.current_version_id ?? versions[0]?.id ?? null;
        const detailV = versions.find((v) => v.id === detailId) ?? null;
        const detailIsLatest = detailV ? skill.current_version_id === detailV.id : false;
        const detailIsSelected = detailV ? selected === detailV.id : false;
        // A missing status is the legacy, already-approved shape from before per-version states.
        const detailIsInstallable = detailV?.status == null || detailV.status === "active";
        const vPageUrl = `${installBase}/skill/${slug}`;
        const vInstallCmd = detailV
          ? `curl -sSL ${installBase}/install_skill/${slug}/${detailV.id} | tar -xz -C ${installDir}/skills/`
          : "";
        return (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={() => setShowHistory(false)}
          >
            <div
              className="flex w-full max-w-[860px] flex-col rounded-2xl border border-white/10 bg-[#111] shadow-2xl"
              style={{ maxHeight: "80vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between border-b border-white/[0.08] px-6 py-4">
                <div>
                  <h3 className="font-display font-semibold">Version history</h3>
                  <p className="text-xs text-text/40">{versions.length} {versions.length === 1 ? "version" : "versions"} · {skill.name}</p>
                </div>
                <button
                  onClick={() => setShowHistory(false)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-text/40 transition hover:bg-white/10 hover:text-text"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Two-column body */}
              <div className="grid min-h-0 flex-1 grid-cols-[260px_1fr] divide-x divide-white/[0.06]">

                {/* ── Left: version list ── */}
                <div className="overflow-y-auto p-3">
                  {/* Gradient timeline line */}
                  <div className="relative">
                    <div
                      className="pointer-events-none absolute left-[19px] w-px"
                      style={{
                        top: 16, bottom: 16,
                        background: "linear-gradient(to bottom, rgba(99,102,241,0.6) 0%, rgba(99,102,241,0.1) 70%, transparent 100%)",
                      }}
                    />
                    <div className="space-y-0.5">
                      {versions.map((v) => {
                        const isLatest = skill.current_version_id === v.id;
                        const isActive = detailId === v.id;
                        const isSelected = selected === v.id;
                        const date = _ts(v.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
                        return (
                          <button
                            key={v.id}
                            onClick={() => { setRestoreError(null); setExpandedVersion(isActive ? null : v.id); }}
                            className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                              isActive
                                ? "bg-primary/10 ring-1 ring-inset ring-primary/20"
                                : "hover:bg-white/[0.04]"
                            }`}
                          >
                            {/* Dot */}
                            <div className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 ${
                              isLatest ? "border-green-400 bg-green-400/20" : "border-primary/40 bg-primary/10"
                            }`}>
                              {isLatest && <div className="h-2 w-2 rounded-full bg-green-400" />}
                            </div>
                            {/* Text */}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1">
                                <span className={`text-sm font-bold tabular-nums ${isLatest ? "text-green-400" : "text-text/80"}`}>
                                  v{v.version}
                                </span>
                                {isLatest && (
                                  <span className="rounded-full bg-green-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-green-400">current</span>
                                )}
                                {isSelected && !isLatest && (
                                  <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-[10px] font-semibold text-primary">active</span>
                                )}
                                {v.restored_from && (
                                  <span className="rounded-full bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-orange-400">restored</span>
                                )}
                                {v.status && v.status !== "active" && (
                                  <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300">
                                    {v.status === "pending" ? "security review" : v.status.replace("_", " ")}
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-text/35">{date}</p>
                              {v.changelog && (
                                <p className="mt-0.5 line-clamp-1 text-xs text-text/45">{v.changelog}</p>
                              )}
                            </div>
                            {/* Active indicator */}
                            {isActive && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mt-1 shrink-0 text-primary/60">
                                <path d="M9 18l6-6-6-6" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* ── Right: detail panel ── */}
                <div className="overflow-y-auto">
                  {detailV ? (
                    <div className="flex flex-col gap-6 p-6">
                      {/* Version heading */}
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`text-xl font-bold tabular-nums ${detailIsLatest ? "text-green-400" : "text-text"}`}>
                              v{detailV.version}
                            </span>
                            {detailIsLatest && (
                              <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-xs font-semibold text-green-400">current</span>
                            )}
                            {detailIsSelected && !detailIsLatest && (
                              <span className="rounded-full bg-primary/20 px-2 py-0.5 text-xs font-semibold text-primary">active</span>
                            )}
                            {detailV.restored_from && (
                              <span className="rounded-full bg-orange-500/15 px-2 py-0.5 text-xs font-semibold text-orange-400">restored</span>
                            )}
                            {detailV.status && detailV.status !== "active" && (
                              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-300">
                                {detailV.status === "pending" ? "security review" : detailV.status.replace("_", " ")}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-text/40">
                            {_ts(detailV.created_at).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                          </p>
                        </div>
                        {/* Action buttons */}
                        <div className="flex shrink-0 items-center gap-2">
                          {detailIsInstallable && !detailIsSelected && (
                            <button
                              onClick={() => { onSelectVersion(detailV.id); setShowHistory(false); }}
                              className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-medium text-text/70 transition hover:border-white/30 hover:text-text"
                            >
                              ↓ Use this version
                            </button>
                          )}
                          {isOwner && detailIsInstallable && !detailIsLatest && (
                            <button
                              onClick={() => onRestore(detailV.id)}
                              disabled={busy}
                              className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-medium text-text/70 transition hover:border-white/30 hover:text-text disabled:opacity-40"
                            >
                              ↩ Restore
                            </button>
                          )}
                        </div>
                      </div>

                      <ErrorNotice error={restoreError} />

                      {/* Changelog */}
                      {detailV.changelog ? (
                        <div>
                          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text/35">What's new</p>
                          <p className="text-sm leading-relaxed text-text/70">{detailV.changelog}</p>
                        </div>
                      ) : (
                        <p className="text-xs italic text-text/25">No release notes for this version.</p>
                      )}

                      {/* Divider */}
                      <div className="border-t border-white/[0.06]" />

                      {/* Share section */}
                      {detailIsInstallable ? (
                        <div className="space-y-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-text/35">Share v{detailV.version}</p>

                        {/* Skill page URL */}
                        <div>
                          <p className="mb-1.5 text-[11px] text-text/40">Skill page</p>
                          <div className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2">
                            <span className="flex-1 truncate font-mono text-xs text-text/55">{vPageUrl}</span>
                            <button
                              onClick={() => copyVersionItem(vPageUrl, `url-${detailV.id}`)}
                              className="flex shrink-0 items-center gap-1 text-text/35 transition hover:text-text/70"
                              title="Copy link"
                            >
                              {versionCopied === `url-${detailV.id}` ? (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-500"><path d="M20 6L9 17l-5-5" /></svg>
                              ) : (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Install command */}
                        <div>
                          <p className="mb-1.5 text-[11px] text-text/40">Install v{detailV.version}</p>
                          <div className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2">
                            <span className="flex-1 truncate font-mono text-xs text-text/55">{vInstallCmd}</span>
                            <button
                              onClick={() => {
                                copyVersionItem(vInstallCmd, `install-${detailV.id}`);
                                trackSkillInstallCopy(slug, "command");
                              }}
                              className="flex shrink-0 items-center gap-1 text-text/35 transition hover:text-text/70"
                              title="Copy install command"
                            >
                              {versionCopied === `install-${detailV.id}` ? (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-500"><path d="M20 6L9 17l-5-5" /></svg>
                              ) : (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Platform share buttons */}
                        <div>
                          <p className="mb-2 text-[11px] text-text/40">Share on</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => shareVersion("linkedin", detailV.id, detailV.version)}
                              title="Copy post & open LinkedIn"
                              className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-white transition hover:opacity-85"
                              style={{ background: "#0077b5" }}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                              LinkedIn
                            </button>
                            <button
                              onClick={() => shareVersion("x", detailV.id, detailV.version)}
                              title="Open X with post pre-filled"
                              className="flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-xs font-medium text-text/70 transition hover:bg-white/5 hover:text-text"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.258 5.632 5.907-5.632Zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                              X (Twitter)
                            </button>
                          </div>
                          <p className="mt-2 text-[11px] text-text/25">
                            LinkedIn copies the post to clipboard — paste with ⌘V / Ctrl+V after it opens.
                          </p>
                        </div>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-4 text-sm text-amber-100/80">
                          {detailV.status === "pending"
                            ? "This restore is being security-reviewed. It cannot be used, shared, or installed until it passes."
                            : "This version is not installable because it did not become an approved current version."}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex h-full items-center justify-center p-8">
                      <p className="text-sm text-text/30">Select a version to see details</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── ACTIONS TAB ── */}
      {tab === "actions" && isOwner && (
        <main className="flex-1 px-6 py-8 sm:px-8">
          <div className="space-y-8">
            <section>
              <h2 className="border-b border-black/10 pb-2.5 font-display text-base font-semibold dark:border-white/10">Governance</h2>
              <div className="mt-4 space-y-2">
                <div className="flex items-start justify-between gap-4 rounded-xl border border-black/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/[0.02]">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Accept suggestions</p>
                    <p className="mt-0.5 text-xs text-text/50">
                      {skill.accept_suggestions
                        ? "Others can propose changes. You review before merging."
                        : "Skill is published and stable — no incoming suggestions."}
                    </p>
                  </div>
                  <button
                    onClick={onToggleSuggestions}
                    disabled={savingSettings}
                    aria-pressed={skill.accept_suggestions}
                    className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 transition-colors disabled:opacity-50 ${
                      skill.accept_suggestions ? "border-primary bg-primary" : "border-white/20 bg-white/10"
                    }`}
                  >
                    <span
                      className={`pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                        skill.accept_suggestions ? "translate-x-[14px]" : "translate-x-[1px]"
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-start justify-between gap-4 rounded-xl border border-black/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/[0.02]">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Discussions</p>
                    <p className="mt-0.5 text-xs text-text/50">
                      {skill.discussions_enabled
                        ? "Anyone can open questions, bug reports, and reviews."
                        : "Turned off — no new threads or replies. Existing discussions stay visible."}
                    </p>
                  </div>
                  <button
                    onClick={onToggleDiscussions}
                    disabled={savingSettings}
                    aria-pressed={skill.discussions_enabled}
                    className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 transition-colors disabled:opacity-50 ${
                      skill.discussions_enabled ? "border-primary bg-primary" : "border-white/20 bg-white/10"
                    }`}
                  >
                    <span
                      className={`pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                        skill.discussions_enabled ? "translate-x-[14px]" : "translate-x-[1px]"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </section>

            <section>
              <h2 className="flex items-center gap-2 border-b border-black/10 pb-2.5 font-display text-base font-semibold dark:border-white/10">
                <GithubMark size={16} /> GitHub
              </h2>
              <div className="mt-4">
                <GithubPanel slug={slug} skill={skill} accessToken={accessToken} onChanged={setSkill} />
              </div>
            </section>

            <section>
              <h2 className="border-b border-black/10 pb-2.5 font-display text-base font-semibold dark:border-white/10">Visibility</h2>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {([
                  { v: "public" as const, label: "Public", desc: "Anyone can find and install it." },
                  { v: "private" as const, label: "Private", desc: "Only you and people you share it with can see it." },
                ]).map((o) => {
                  const active = skill.visibility === o.v;
                  // A default skill stays private (it lives in users' libraries, not the catalog).
                  const locked = !!skill.is_default && o.v === "public";
                  return (
                    <button
                      key={o.v}
                      onClick={() => setVisibility(o.v)}
                      disabled={busy || locked}
                      title={locked ? "Default skills stay private. Remove it from the default skills first." : undefined}
                      className={`flex items-start gap-3 rounded-xl border p-4 text-left transition disabled:opacity-60 ${
                        active ? "border-primary/60 bg-primary/10" : "border-black/10 bg-black/[0.02] hover:border-black/20 dark:border-white/10 dark:bg-white/[0.02] dark:hover:border-white/25"
                      }`}
                    >
                      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${active ? "border-primary bg-primary text-white" : "border-black/25 dark:border-white/30"}`}>
                        {active && (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                        )}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{o.label}</span>
                        <span className="mt-0.5 block text-xs text-text/50">{o.desc}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {/* Team sharing — same card style, independent of Public/Private. Toggle on → pick teams. */}
              {(() => {
                const teamGrants = grants.filter((g) => g.kind === "team");
                const on = teamGrants.length > 0 || teamPanelOpen;
                return (
                  <>
                    <div className="mt-2 rounded-xl border border-black/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/[0.02]">
                      <div className="flex items-center justify-between gap-3">
                        <span className="min-w-0">
                          <span className="block text-sm font-medium">Team</span>
                          <span className="mt-0.5 block text-xs text-text/50">Share this skill with teams you belong to.</span>
                        </span>
                        <button
                          role="switch"
                          aria-checked={on}
                          onClick={toggleTeamSharing}
                          disabled={busy}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition disabled:opacity-50 ${on ? "bg-primary" : "bg-black/15 dark:bg-white/20"}`}
                        >
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                        </button>
                      </div>

                      {on && (
                        <div className="mt-4 border-t border-black/10 pt-3 dark:border-white/10">
                          {myTeams.length === 0 ? (
                            <p className="text-sm text-text/50">You're not part of any team yet.</p>
                          ) : (
                            <ul className="space-y-2">
                              {[...myTeams].sort((a, b) => a.name.localeCompare(b.name)).map((t) => {
                                const has = teamGrants.some((g) => g.slug === t.slug);
                                return (
                                  <li key={t.id} className="flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-2 text-sm">
                                      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-xs font-semibold text-accent">{t.name.charAt(0).toUpperCase()}</span>
                                      {t.name}
                                    </span>
                                    <button
                                      onClick={() => toggleTeamAccess(t, has)}
                                      disabled={busy}
                                      className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-medium transition disabled:opacity-50 ${
                                        has
                                          ? "border-[var(--border)] text-text/70 hover:border-red-400 hover:text-red-500"
                                          : "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
                                      }`}
                                    >
                                      {has ? "Remove access" : "Give access"}
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}

              {publishError && (
                <div ref={publishErrorRef} className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                  <p className="text-sm font-semibold text-amber-400">Not quite ready to share</p>
                  <p className="mt-1 text-sm text-amber-700 dark:text-amber-200/80">{publishError}</p>
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-red-500/25 bg-red-500/[0.04] p-6">
              <h2 className="border-b border-red-500/20 pb-2.5 font-display text-base font-semibold text-red-400">Danger zone</h2>
              <p className="mt-3 text-sm text-text/55">
                Deleting this skill removes it <b className="text-text/80">permanently</b> — every version, suggestion, and its files. This <b className="text-text/80">cannot be undone</b>.
              </p>
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="mt-4 rounded-lg border border-red-500/40 px-4 py-2 text-sm text-red-400 transition hover:bg-red-500/10"
                >
                  Delete skill
                </button>
              ) : (
                <div className="mt-4 rounded-xl border border-red-500/40 bg-red-500/[0.06] p-4">
                  <p className="text-sm">Delete <b>{skill.name}</b> permanently? This removes every version and suggestion.</p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={onDelete} disabled={busy} className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50">
                      {busy ? "Deleting…" : "Yes, delete permanently"}
                    </button>
                    <button onClick={() => setConfirmDelete(false)} disabled={busy} className="rounded-lg border border-black/15 px-4 py-2 text-sm transition hover:bg-black/5 disabled:opacity-50 dark:border-white/15 dark:hover:bg-white/5">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        </main>
      )}

    </div>
  );
}


function GithubPanel({
  slug,
  skill,
  accessToken,
  onChanged,
}: {
  slug: string;
  skill: SkillDetail;
  accessToken: string | null;
  onChanged: (s: SkillDetail) => void;
}) {
  const gh = skill.github;
  const navigate = useNavigate();
  const skillPath = useSkillPath();
  const [connected, setConnected] = useState<boolean | null>(null);
  const [target, setTarget] = useState<GithubTarget>({ repo: "", branch: "main", subpath: "" });
  const [busy, setBusy] = useState<null | "link" | "push" | "unlink" | "resync">(null);
  const [error, setError] = useState<DescribedError | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [showResolver, setShowResolver] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [autoPush, setAutoPush] = useState(false);

  // Only need the connection check when the skill isn't linked yet (to show the link form).
  useEffect(() => {
    if (gh || connected !== null || !accessToken) return;
    getGithubStatus(accessToken)
      .then((s) => setConnected(s.connected && s.token_valid !== false))
      .catch(() => setConnected(false));
  }, [gh, connected, accessToken]);

  if (!accessToken) return null;

  async function onLink() {
    if (!target.repo) {
      setError({ tone: "error", title: "Choose a repository", detail: "Pick a repository to link this skill to." });
      return;
    }
    setBusy("link");
    setError(null);
    try {
      onChanged(await linkSkillGithub(slug, { repo: target.repo, branch: target.branch, subpath: target.subpath.trim(), auto_push: autoPush }, accessToken!));
    } catch (e) {
      setError(describeGithubError(e, "link"));
    } finally {
      setBusy(null);
    }
  }

  async function onPush() {
    setBusy("push");
    setError(null);
    setNote(null);
    try {
      const updated = await pushSkillGithub(slug, accessToken!);
      onChanged(updated);
      setNote(updated.github?.open_pr_url ? "Pull request opened on GitHub." : "Pushed to GitHub.");
    } catch (e) {
      setError(describeGithubError(e, "push"));
    } finally {
      setBusy(null);
    }
  }

  async function onResync() {
    setBusy("resync");
    setError(null);
    setNote(null);
    try {
      const updated = await resyncSkillGithub(slug, accessToken!);
      onChanged(updated);
      setNote(updated.github?.sync_error ? null : "Re-synced from GitHub.");
    } catch (e) {
      setError(describeGithubError(e, "push"));
    } finally {
      setBusy(null);
    }
  }

  async function onUnlink() {
    setConfirmUnlink(false);
    setBusy("unlink");
    setError(null);
    try {
      onChanged(await unlinkSkillGithub(slug, accessToken!));
      setConnected(null);
    } catch (e) {
      setError(describeGithubError(e, "unlink"));
    } finally {
      setBusy(null);
    }
  }

  async function onToggleAutoPush(enabled: boolean) {
    setError(null);
    try {
      onChanged(await updateGithubSettings(slug, enabled, accessToken!));
    } catch (e) {
      setError(describeGithubError(e, "push"));
    }
  }

  // ── Not linked: show the link form ──
  if (!gh) {
    if (connected === false) {
      return (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-200">
          Connect your GitHub account first in{" "}
          <Link to="/settings" className="font-medium underline">Settings</Link>.
        </div>
      );
    }
    if (connected === null) {
      return <p className="text-sm text-text/45">Checking your GitHub connection…</p>;
    }
    return (
      <div className="rounded-xl border border-black/10 bg-black/[0.02] p-5 dark:border-white/10 dark:bg-white/[0.02]">
        <div>
          <h4 className="text-sm font-semibold text-text">Link to a repository</h4>
          <p className="mt-0.5 text-xs text-text/45">
            Syncs through pull requests, so every change stays reviewable.
          </p>
        </div>

        <div className="mt-5">
          <GithubPicker token={accessToken} value={target} onChange={setTarget} disabled={busy !== null} />
        </div>

        <label className="mt-4 flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={autoPush}
            onChange={(e) => setAutoPush(e.target.checked)}
            disabled={busy !== null}
            className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
          />
          <span className="text-sm">
            <span className="font-medium text-text/85">Auto-sync on change</span>
            <span className="mt-0.5 block text-xs text-text/45">
              Open a pull request automatically whenever this skill changes.
            </span>
          </span>
        </label>

        <ErrorNotice error={error} className="mt-3" />

        <button
          onClick={onLink}
          disabled={busy !== null || !target.repo}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
        >
          <GithubMark size={15} /> {busy === "link" ? "Linking…" : "Link to GitHub"}
        </button>
      </div>
    );
  }

  // ── Linked: show status + actions ──
  const subpathLabel = gh.subpath ? gh.subpath : "repo root";
  return (
    <div className="rounded-xl border border-black/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/[0.02]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <a
            href={githubLinkUrl(gh)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-text hover:underline"
          >
            <GithubMark size={14} /> {gh.repo}
            <span className="text-text/40">@{gh.branch}</span>
          </a>
          <p className="mt-1 text-xs text-text/45">
            Folder: <span className="text-text/60">{subpathLabel}</span> ·{" "}
            {gh.auto_push
              ? "A pull request is opened automatically when this skill changes."
              : "Sync is manual — use Push to open a pull request."}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <SyncStateBadge state={gh.sync_state} />
          {gh.open_pr_url && (
            <a
              href={gh.open_pr_url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-400 transition hover:bg-blue-500/20"
            >
              PR open ↗
            </a>
          )}
        </div>
      </div>

      <label className="mt-3 flex items-center gap-2.5 text-sm text-text/70">
        <input
          type="checkbox"
          checked={!!gh.auto_push}
          onChange={(e) => onToggleAutoPush(e.target.checked)}
          disabled={busy !== null}
          className="h-4 w-4 shrink-0 accent-primary"
        />
        Automatically open a pull request when this skill changes
      </label>

      {gh.link_broken && (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          Can’t reach <span className="font-medium">{gh.repo}</span> — it may have been deleted or
          renamed.{" "}
          <button
            onClick={() => setConfirmUnlink(true)}
            className="font-medium text-red-100 underline underline-offset-2 hover:text-white"
          >
            Unlink
          </button>
          .
        </div>
      )}
      {gh.sync_error && !gh.link_broken && (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          Couldn’t sync the latest GitHub change — it may have been a temporary glitch.{" "}
          <button
            onClick={onResync}
            disabled={busy !== null}
            className="font-medium text-red-100 underline underline-offset-2 hover:text-white disabled:opacity-50"
          >
            {busy === "resync" ? "Retrying…" : "Retry"}
          </button>
          .
        </div>
      )}
      {gh.sync_state === "github_ahead" && (
        <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-200">
          GitHub has new changes waiting to be pulled in. Nothing is applied until you{" "}
          <Link to={skillPath(slug, "?tab=suggestions")} className="font-medium underline">
            review and approve them in Suggestions
          </Link>
          .
        </div>
      )}
      {gh.sync_state === "conflict" && (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          GitHub and the platform both changed since they were last in sync.
          <button
            onClick={() => setShowResolver(true)}
            className="ml-1 font-medium text-red-200 underline underline-offset-2 hover:text-white"
          >
            Resolve conflict
          </button>
        </div>
      )}
      {showResolver && accessToken && (
        <ConflictResolverModal
          slug={slug}
          accessToken={accessToken}
          onClose={() => setShowResolver(false)}
          onResolved={(suggestionId) => {
            setShowResolver(false);
            navigate(skillPath(slug, `/suggestions/${suggestionId}`)); // → the review pipeline
          }}
        />
      )}

      {note && <p className="mt-3 text-sm text-green-400">{note}</p>}
      <ErrorNotice error={error} className="mt-3" />

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={onPush}
          disabled={busy !== null}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
        >
          <GithubMark size={14} /> {busy === "push" ? "Pushing…" : "Push to GitHub"}
        </button>
        {gh.open_pr_url && (
          <a
            href={gh.open_pr_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-text/80 transition hover:bg-white/5"
          >
            View PR
          </a>
        )}
        <button
          onClick={() => setConfirmUnlink(true)}
          disabled={busy !== null}
          className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-text/70 transition hover:bg-white/5 disabled:opacity-50"
        >
          {busy === "unlink" ? "Unlinking…" : "Unlink"}
        </button>
      </div>

      {confirmUnlink && (
        <ConfirmModal
          title="Unlink from GitHub?"
          message="This skill will be disconnected from its repository and its push webhook removed. Your skill and its versions stay on the platform — only the GitHub link is removed."
          confirmLabel="Unlink"
          destructive
          busy={busy === "unlink"}
          onConfirm={onUnlink}
          onCancel={() => setConfirmUnlink(false)}
        />
      )}
    </div>
  );
}

// Threshold → CSS-variable colour for score text (theme-aware; see --radar-score-* in index.css).
const scoreVar = (s: number) =>
  s >= 70 ? "var(--radar-score-strong)" : s >= 40 ? "var(--radar-score-fair)" : "var(--radar-score-weak)";

// Forced-dark override for the radar's theme tokens — applied when tone="dark" so the chart
// stays legible inside the app's always-dark modals even when the global theme is light.
const RADAR_DARK_VARS = {
  "--radar-grid": "rgba(255,255,255,0.28)",
  "--radar-grid-soft": "rgba(255,255,255,0.22)",
  "--radar-tick": "#e2e8f0",
  "--radar-label": "#cbd5e1",
  "--radar-dot-fill": "#d1fae5",
  "--radar-score-strong": "#4ade80",
  "--radar-score-fair": "#fbbf24",
  "--radar-score-weak": "#f87171",
  "--radar-fill-0": "#34d399",
  "--radar-fill-1": "#059669",
  "--radar-stroke-0": "#6ee7b7",
  "--radar-stroke-1": "#34d399",
  "--radar-glow": "rgba(52,211,153,0.14)",
  "--radar-tick-halo": "rgba(10,12,18,0.85)",
} as React.CSSProperties;

// Hand-rolled SVG radar ("spider") chart of the six security dimensions. No chart
// library — matches the inline-SVG approach used elsewhere in the app. An emerald
// score polygon (+ soft green glow) traces the actual scores over neutral concentric
// rings; per-dimension weak/fair/strong is shown by the threshold-coloured % labels.
// All colours come from theme-aware --radar-* tokens (light or dark); `tone="dark"`
// forces the dark palette for the app's always-dark contexts.
function SecurityRadar({
  dims,
  security,
  size = 360,
  tone = "auto",
}: {
  dims: { key: keyof NonNullable<SkillDetail["security"]>; label: string }[];
  security: NonNullable<SkillDetail["security"]>;
  size?: number;
  tone?: "auto" | "dark";
}) {
  const rawId = useId();
  const uid = rawId.replace(/:/g, ""); // colons are awkward inside url(#…) references
  const pts = dims
    .map((d) => ({ label: d.label, score: (security[d.key] as { score: number } | null)?.score ?? null }))
    .filter((d): d is { label: string; score: number } => d.score !== null);
  if (pts.length < 3) return null; // need a polygon

  const n = pts.length;
  const cx = size / 2;
  const cy = size / 2;
  const R = size * 0.3;
  const angle = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const at = (i: number, r: number): [number, number] => [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
  const ringPts = (r: number) => pts.map((_, i) => at(i, r).join(",")).join(" ");
  const dataPoly = pts.map((d, i) => at(i, (R * d.score) / 100).join(",")).join(" ");

  return (
    <div className="flex w-full max-w-[360px] flex-col items-center gap-3" style={tone === "dark" ? RADAR_DARK_VARS : undefined}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block h-auto w-full overflow-visible">
        <defs>
          <radialGradient id={`${uid}-bg`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" style={{ stopColor: "var(--radar-glow)" }} />
            <stop offset="70%" style={{ stopColor: "var(--radar-glow)", stopOpacity: 0 }} />
          </radialGradient>
          {/* Soft legend-colour banding: red core (0-40) → amber (40-70) → green (70-100),
              in user space so the transitions sit at the real score thresholds. Light so
              the score polygon still dominates. */}
          <radialGradient id={`${uid}-zones`} gradientUnits="userSpaceOnUse" cx={cx} cy={cy} r={R}>
            <stop offset="0" stopColor="#ef4444" stopOpacity={0.16} />
            <stop offset="0.38" stopColor="#ef4444" stopOpacity={0.1} />
            <stop offset="0.46" stopColor="#f59e0b" stopOpacity={0.12} />
            <stop offset="0.66" stopColor="#f59e0b" stopOpacity={0.1} />
            <stop offset="0.74" stopColor="#22c55e" stopOpacity={0.13} />
            <stop offset="1" stopColor="#22c55e" stopOpacity={0.17} />
          </radialGradient>
          <linearGradient id={`${uid}-fill`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: "var(--radar-fill-0)", stopOpacity: 0.42 }} />
            <stop offset="100%" style={{ stopColor: "var(--radar-fill-1)", stopOpacity: 0.16 }} />
          </linearGradient>
          <linearGradient id={`${uid}-stroke`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" style={{ stopColor: "var(--radar-stroke-0)" }} />
            <stop offset="100%" style={{ stopColor: "var(--radar-stroke-1)" }} />
          </linearGradient>
          <filter id={`${uid}-glow`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="2.5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Soft green glow + gentle red→amber→green threshold banding behind the plot */}
        <circle cx={cx} cy={cy} r={R} fill={`url(#${uid}-bg)`} />
        <polygon points={ringPts(R)} fill={`url(#${uid}-zones)`} />

        {/* Concentric grid rings + spokes (theme-aware, neutral) */}
        {[0.2, 0.4, 0.6, 0.8, 1].map((f) => (
          <polygon key={f} points={ringPts(R * f)} fill="none" style={{ stroke: "var(--radar-grid)" }} strokeWidth={0.8} />
        ))}
        {pts.map((_, i) => {
          const [x, y] = at(i, R);
          return <line key={i} x1={cx} y1={cy} x2={x} y2={y} style={{ stroke: "var(--radar-grid-soft)" }} strokeWidth={1} />;
        })}

        {/* Measured-score polygon — gradient fill + glow so it reads as the figure */}
        <polygon points={dataPoly} fill={`url(#${uid}-fill)`} stroke={`url(#${uid}-stroke)`} strokeWidth={2.5} strokeLinejoin="round" filter={`url(#${uid}-glow)`} />
        {pts.map((d, i) => {
          const [x, y] = at(i, (R * d.score) / 100);
          // Node colour aligns with its score (green ≥70 / amber ≥40 / red below) —
          // matching the threshold banding and the % labels.
          return <circle key={i} cx={x} cy={y} r={4} strokeWidth={1.75} style={{ fill: scoreVar(d.score), stroke: "var(--radar-dot-fill)" }} />;
        })}

        {/* Radial scale ticks up the top spoke — drawn LAST (on top of the polygon) with a
            theme-matching halo so the numbers stay legible even where the fill covers them */}
        {[20, 40, 60, 80].map((v) => (
          <text
            key={v}
            x={cx + 5}
            y={cy - (R * v) / 100}
            dominantBaseline="middle"
            className="text-[9px] font-medium"
            style={{ fill: "var(--radar-tick)", paintOrder: "stroke", stroke: "var(--radar-tick-halo)", strokeWidth: 2, strokeLinejoin: "round" }}
          >
            {v}
          </text>
        ))}

        {/* Outside labels: dimension name + threshold-coloured score, stacked. Anchored
            to the outer ring (not the vertex) so labels never collide with the polygon,
            even at 0% or 100%. The colour doubles as a pass/warn/fail legend. */}
        {pts.map((d, i) => {
          const [x, y] = at(i, R + 17);
          const c = Math.cos(angle(i));
          const anchor = c > 0.25 ? "start" : c < -0.25 ? "end" : "middle";
          return (
            <g key={i}>
              <text x={x} y={y - 6} textAnchor={anchor} dominantBaseline="middle" className="text-[10px] font-medium" style={{ fill: "var(--radar-label)" }}>
                {d.label}
              </text>
              <text x={x} y={y + 7} textAnchor={anchor} dominantBaseline="middle" className="text-[11px] font-bold" style={{ fill: scoreVar(d.score) }}>
                {d.score}%
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend — decodes the zone colours and the score line */}
      <div className="flex flex-wrap items-center justify-center gap-x-3.5 gap-y-1.5 text-[10px] text-text/50">
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-red-500" />0–39 Weak</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-yellow-500" />40–69 Fair</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-green-500" />70–100 Strong</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-3 rounded-full" style={{ background: "var(--radar-stroke-0)" }} />Score</span>
      </div>
    </div>
  );
}

// Fork provenance as an indented lineage tree (parent → this skill → forks),
// replacing the flat list so a skill's descent reads at a glance. Children scale
// to any count via the classic border-left / horizontal-tick tree connectors.
function ForkTree({
  name,
  parentName,
  forks,
  forkCount,
  pathFor,
  onOpen,
}: {
  name: string;
  parentName: string | null;
  forks: Skill[];
  forkCount: number;
  pathFor: (slug: string) => string;
  onOpen: () => void;
}) {
  return (
    <div className="text-sm">
      {parentName && (
        <>
          <div className="flex items-center gap-2 text-text/50">
            <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full border border-white/25" />
            <span className="truncate">{parentName}</span>
            <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-text/40">parent</span>
          </div>
          <div className="ml-[6px] h-4 w-px bg-white/15" />
        </>
      )}

      <div className="inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5">
        <span className="flex h-2.5 w-2.5 rounded-full bg-primary" />
        <span className="font-semibold text-text/90">{name}</span>
        <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-[10px] font-medium text-primary">this skill</span>
      </div>

      {forks.length > 0 && (
        <div className="ml-[6px] mt-1 border-l border-white/15">
          {forks.map((f) => (
            <Link
              key={f.id}
              to={pathFor(f.slug)}
              onClick={onOpen}
              className="group relative flex items-start gap-2 rounded-r-lg py-2 pl-5 pr-3 transition hover:bg-white/[0.04]"
            >
              <span className="absolute left-0 top-[18px] h-px w-4 bg-white/15" />
              <span className="mt-1 flex h-2 w-2 shrink-0 rounded-full border border-primary/50 bg-primary/10" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-text/80">{f.name}</p>
                {f.owner_name && <p className="mt-0.5 text-xs text-text/45">by {f.owner_name}</p>}
                {f.summary && <p className="mt-1 line-clamp-2 text-xs text-text/40">{f.summary}</p>}
              </div>
              <span className="ml-auto shrink-0 self-center text-xs text-text/30 transition group-hover:text-primary">View →</span>
            </Link>
          ))}
        </div>
      )}

      {forkCount > forks.length && (
        <p className="ml-5 mt-2 text-xs text-text/35">
          +{forkCount - forks.length} more fork{forkCount - forks.length === 1 ? "" : "s"} not public yet
        </p>
      )}
    </div>
  );
}

// Design-system "eyebrow" label: Space Mono, uppercase, small orange dash prefix.
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-text/45">
      <span className="h-px w-5 bg-primary" />
      {children}
    </span>
  );
}

// 1536 → "1.5 KB" — used by the "What's inside" contents card.
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Compact security card for the install rail — a score ring, the verdict, and a way into the full
// rubric. It sits directly above the install action it gates.
//
// The verdict comes from `security.passed`, NOT from counting dimensions above some threshold. The
// backend gate (agents/security.py, pipeline v3) is "no confirmed critical finding AND no dimension
// below 40" — so the old "N of 6 passed" readout, which counted dimensions >= 70, could show
// "3 of 6 passed" on a skill that had genuinely passed review. That read as a failure.
function SecurityRailCard({
  security,
  onOpen,
}: {
  security: NonNullable<SkillDetail["security"]>;
  onOpen: () => void;
}) {
  const score = security.score;
  const tone =
    score == null ? { ring: "rgba(255,255,255,.25)", text: "text-text/45" }
    : score >= 80 ? { ring: "#22c55e", text: "text-green-500 dark:text-green-400" }
    : score >= 60 ? { ring: "#f59e0b", text: "text-amber-600 dark:text-amber-400" }
    : { ring: "#ef4444", text: "text-red-500 dark:text-red-400" };
  const verdict =
    score == null ? "Scan pending"
    : security.passed ? "Passed all checks"
    : "Concerns flagged";
  const r = 19;
  const c = 2 * Math.PI * r;

  return (
    <button
      type="button"
      onClick={onOpen}
      title="View the full security report"
      className="group w-full overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] text-left transition hover:border-white/25"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="relative flex h-12 w-12 shrink-0 items-center justify-center">
          <svg width="48" height="48" className="-rotate-90">
            <circle cx="24" cy="24" r={r} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="3.5" />
            {score != null && (
              <circle
                cx="24" cy="24" r={r} fill="none" stroke={tone.ring} strokeWidth="3.5" strokeLinecap="round"
                strokeDasharray={c} strokeDashoffset={c * (1 - Math.max(0, Math.min(100, score)) / 100)}
              />
            )}
          </svg>
          <span className="absolute text-sm font-bold tabular-nums text-text">{score ?? "–"}</span>
        </span>
        <span className="min-w-0">
          <span className="block font-display text-sm font-medium text-text">Security review</span>
          <span className={`mt-0.5 block text-xs font-medium ${tone.text}`}>{verdict}</span>
        </span>
      </div>
      <span className="flex items-center justify-between border-t border-[var(--border)] px-4 py-2 font-display text-xs font-medium text-text/45 transition group-hover:text-primary">
        View full report
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
      </span>
    </button>
  );
}

function SecurityRubric({
  security,
  open: openProp,
  onOpenChange,
}: {
  security: NonNullable<SkillDetail["security"]>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  // Controlled/uncontrolled: use the parent's state if provided, else our own.
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  // The detailed report (per-dimension breakdown, findings) is collapsed by
  // default so the radar reads as the headline; user expands to see specifics.
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Always start collapsed each time the modal opens.
  useEffect(() => {
    if (!open) setDetailsOpen(false);
  }, [open]);
  const dims = [
    { key: "prompt_injection" as const, label: "Prompt Injection", short: "Prompt Inj.", icon: "🛡" },
    { key: "data_exfiltration" as const, label: "Data Exfiltration", short: "Data Exfil.", icon: "📤" },
    { key: "code_execution" as const, label: "Code Execution", short: "Code Exec.", icon: "⚙️" },
    { key: "supply_chain" as const, label: "Supply Chain", short: "Supply Chain", icon: "🔗" },
    { key: "auth_secrets" as const, label: "Auth & Secrets", short: "Auth", icon: "🔑" },
    { key: "network_safety" as const, label: "Network Safety", short: "Network", icon: "🌐" },
  ];

  const totalScore = security.score ?? 0;
  const totalColor = totalScore >= 70 ? "text-green-400" : totalScore >= 40 ? "text-yellow-400" : "text-red-400";
  const totalRing = totalScore >= 70 ? "border-green-500/40" : totalScore >= 40 ? "border-yellow-500/40" : "border-red-500/40";

  return (
    <>
      {/* Own trigger button — only when nobody else is controlling `open`. In this file,
          SecurityIndicator already IS the trigger (the score card in the header), and passes
          open/onOpenChange down, so this would otherwise show a second redundant
          "Security rubric" button right next to it. */}
      {openProp === undefined && (
        <button
          onClick={() => setOpen(true)}
          title="View security rubric"
          className="ml-1 inline-flex items-center gap-1 rounded-full border border-primary/50 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary shadow-[0_0_10px_rgba(255,107,26,0.5)] transition hover:border-primary hover:bg-primary/20 hover:shadow-[0_0_14px_rgba(255,107,26,0.7)]"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          Security rubric
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex w-full max-w-[68rem] flex-col rounded-2xl border border-white/10 bg-[#111] shadow-2xl"
            style={{ maxHeight: "90vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header — stays fixed at top, does not scroll */}
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <div className="flex items-center gap-4">
                <div className={`flex h-14 w-14 items-center justify-center rounded-xl border-2 ${totalRing} bg-white/[0.03]`}>
                  <span className={`text-2xl font-bold tabular-nums ${totalColor}`}>{totalScore}</span>
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold">Security Review</h3>
                  <p className="text-xs text-text/40">
                    {security.passed ? "Passed all checks" : "Review flagged concerns"} · scored {totalScore}/100
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-text/40 transition hover:bg-white/10 hover:text-text"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable body — everything below the header scrolls together as one unit */}
            <div className="overflow-y-auto">
              {/* Summary */}
              {security.summary && (
                <div className="border-b border-white/10 px-6 py-3">
                  <p className="text-sm text-text/60">{security.summary}</p>
                </div>
              )}

              {/* Radar — the whole safety "shape" at a glance, above the per-dimension detail */}
              <div className="flex justify-center border-b border-white/10 px-6 py-5">
                <SecurityRadar dims={dims} security={security} />
              </div>

              {/* Detailed report — collapsed into a dropdown; radar stays the headline */}
              <button
                onClick={() => setDetailsOpen((v) => !v)}
                aria-expanded={detailsOpen}
                className="flex w-full items-center justify-between border-t border-white/10 px-6 py-3.5 text-left transition hover:bg-white/[0.03]"
              >
                <span className="flex items-center gap-2">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`text-text/40 transition-transform ${detailsOpen ? "rotate-90" : ""}`}
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                  <span className="text-sm font-semibold text-text/80">
                    Full security report
                  </span>
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`text-text/40 transition-transform ${detailsOpen ? "rotate-180" : ""}`}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {detailsOpen && (
                <>
                  {/* Dimensions grid — 2 columns on ≥sm, stacks to 1 on phones */}
                  <div className="grid grid-cols-1 gap-px border-t border-white/10 bg-white/[0.06] sm:grid-cols-2">
                    {dims.map(({ key, label, icon }) => {
                      const dim = security[key];
                      if (!dim) return null;
                      const pct = dim.score;
                      const barColor = pct >= 70 ? "bg-green-500" : pct >= 40 ? "bg-yellow-500" : "bg-red-500";
                      const scoreColor = pct >= 70 ? "text-green-400" : pct >= 40 ? "text-yellow-400" : "text-red-400";
                      const statusIcon = pct >= 70 ? "✓" : pct >= 40 ? "!" : "✕";
                      const statusBg = pct >= 70 ? "bg-green-500/15 text-green-400" : pct >= 40 ? "bg-yellow-500/15 text-yellow-400" : "bg-red-500/15 text-red-400";
                      return (
                        <div key={key} className="bg-[#111] px-6 py-4">
                          <div className="flex items-center gap-2.5">
                            <span className="text-base">{icon}</span>
                            <span className="flex-1 text-sm font-medium">{label}</span>
                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusBg}`}>{statusIcon}</span>
                            <span className={`w-14 text-right text-sm font-bold tabular-nums ${scoreColor}`}>{pct}/100</span>
                          </div>
                          <div className="mt-2 h-1.5 w-full rounded-full bg-white/10">
                            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                          </div>
                          {dim.findings.length > 0 && (
                            <ul className="mt-3.5 space-y-1">
                              {dim.findings.map((f, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-text/45">
                                  <Bullet size="h-1.5 w-1.5" className="mt-[5px] opacity-45" />
                                  <span className="min-w-0">{f}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Critical findings — only shown if any exist */}
                  {security.critical_findings && security.critical_findings.length > 0 && (
                    <div className="border-t border-red-500/20 bg-red-500/[0.04] px-6 py-4">
                      <h4 className="text-sm font-semibold text-red-400">
                        ⚠ Critical Findings ({security.critical_findings.length})
                      </h4>
                      <ul className="mt-2 space-y-2">
                        {security.critical_findings.map((f, i) => (
                          <li key={i} className="text-xs text-red-200/80">
                            <span className="font-semibold text-red-300">
                              [{f.severity}] {f.dimension}
                            </span>
                            {" — "}{f.description}
                            {f.file && <span className="text-red-300/50"> ({f.file})</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Capability disclosure — what the skill openly does */}
                  {security.capability_disclosure && security.capability_disclosure.length > 0 && (
                    <div className="border-t border-white/10 px-6 py-4">
                      <h4 className="text-sm font-semibold text-text/70">What this skill does</h4>
                      <ul className="mt-2 space-y-1">
                        {security.capability_disclosure.map((c, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-text/50">
                            <Bullet size="h-1.5 w-1.5" className="mt-[5px] opacity-45" />
                            <span className="min-w-0">{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {/* Footer */}
              <div className="border-t border-white/10 px-6 py-3 text-sm text-text/30">
                Reviewed by <span className="font-medium text-primary">SkillPatch Security Agent</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
